import { useState } from 'react';

export default function ContactSection() {
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const formUrl = 'https://readdy.ai/api/form/d9rctdee3oq4jqumtvn0';

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotInput = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotInput && honeypotInput.value.trim() !== '') {
      setStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });
      return;
    }

    if (formData.message.length > 500) {
      setErrorMsg('留言内容不能超过500个字符。');
      setStatus('error');
      return;
    }

    setStatus('loading');
    setErrorMsg('');

    try {
      const formElem = new FormData(form);
      const response = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formElem as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed && parsed.code === 'OK') {
        setStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        const msg = parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText || '提交失败';
        if (msg.includes('spam') || msg.includes('form data is spam')) {
          setErrorMsg('提交失败，请稍后重试。');
        } else {
          setErrorMsg(msg);
        }
        setStatus('error');
      }
    } catch {
      setErrorMsg('网络错误，请检查您的网络连接后重试。');
      setStatus('error');
    }
  };

  return (
    <section className="py-14 md:py-20 px-4 md:px-6" id="contact">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16">
          <div>
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-6">
              联系我们
            </h2>
            <p className="text-foreground-600 leading-relaxed mb-8">
              如果您有任何问题、建议或合作意向，欢迎通过以下方式联系我们。我们的团队会在1-2个工作日内回复您的留言。
            </p>

            <div className="space-y-5">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 flex-shrink-0">
                  <i className="ri-mail-line"></i>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-foreground-900 mb-1">电子邮箱</h4>
                  <p className="text-sm text-foreground-600">contact@jingpinyiqu.com</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-500 flex-shrink-0">
                  <i className="ri-phone-line"></i>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-foreground-900 mb-1">客服电话</h4>
                  <p className="text-sm text-foreground-600">400-888-9999</p>
                  <p className="text-xs text-foreground-500 mt-1">工作日 9:00 - 18:00</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-500 flex-shrink-0">
                  <i className="ri-map-pin-line"></i>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-foreground-900 mb-1">公司地址</h4>
                  <p className="text-sm text-foreground-600">北京市朝阳区建国路88号精品大厦15层</p>
                </div>
              </div>
            </div>
          </div>

          <div>
            <div className="bg-background-100 rounded-lg p-6 md:p-8">
              <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-6">
                发送留言
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form="">
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  readOnly
                  aria-hidden="true"
                  className="honeypot-field"
                />
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                  <input
                    id="contact-name"
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md outline-none focus:border-primary-400 transition-colors duration-200"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                  <input
                    id="contact-email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md outline-none focus:border-primary-400 transition-colors duration-200"
                    placeholder="请输入您的邮箱"
                  />
                </div>
                <div>
                  <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                  <input
                    id="contact-subject"
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    required
                    className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md outline-none focus:border-primary-400 transition-colors duration-200"
                    placeholder="请输入留言主题"
                  />
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言内容
                    <span className="text-foreground-400 font-normal ml-2">（{formData.message.length}/500字符）</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md outline-none focus:border-primary-400 transition-colors duration-200 resize-none"
                    placeholder="请输入您的留言内容（不超过500字符）"
                  />
                </div>
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="w-full py-3 bg-primary-500 text-white text-sm font-medium rounded-md hover:bg-primary-600 transition-colors duration-200 disabled:opacity-60 cursor-pointer whitespace-nowrap"
                >
                  {status === 'loading' ? '发送中...' : '发送留言'}
                </button>
                {status === 'success' && (
                  <p className="text-sm text-secondary-600 font-medium text-center">
                    留言已发送！我们会尽快与您联系。
                  </p>
                )}
                {status === 'error' && errorMsg && (
                  <p className="text-sm text-accent-600 font-medium text-center">
                    {errorMsg}
                  </p>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}